import { useState, useEffect } from "react";
import { PopupModal } from "../../../components/Modal";
import { api } from "../../../helpers/api";
import { ChunkListResponse } from "../../../types/documents";
import Spinner from "../../../components/Spinners";
import CustomButton from "../../../components/Button";
import { ShowToast } from "../../../components/toast";

interface ViewChunksModalProps {
  documentId: string;
  documentName: string;
  onClose: () => void;
}

export function ViewChunksModal({
  documentId,
  documentName,
  onClose,
}: ViewChunksModalProps) {
  const [chunkData, setChunkData] = useState<ChunkListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [error, setError] = useState<string | null>(null);

  const fetchChunks = async (page: number) => {
    try {
      setIsLoading(true);
      setError(null);
      const data = await api.documents.getChunks(documentId, page, 20);
      setChunkData(data);
    } catch (err: any) {
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load chunks";
      setError(errorMessage);
      ShowToast({ message: errorMessage, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchChunks(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, documentId]);

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (chunkData && currentPage < chunkData.pagination.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <PopupModal
      onClose={onClose}
      contentClassName="max-w-4xl w-full mx-auto max-h-[90vh] overflow-hidden flex flex-col"
    >
      <div className="flex flex-col gap-4 h-full">
        {/* Header */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h2 className="text-black text-xl font-semibold">
              Document Chunks
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-black transition-colors"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="text-gray-600 text-sm">
            {documentName}
          </div>
          {chunkData && (
            <div className="text-gray-500 text-xs">
              Strategy: {chunkData.strategy} • Total chunks: {chunkData.chunkCount}
            </div>
          )}
        </div>

        <div className="w-full border-b border-gray-200"></div>

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Spinner className="!text-primary" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 gap-4">
            <span className="text-red-600 text-sm">{error}</span>
            <CustomButton onClick={() => fetchChunks(currentPage)}>
              Retry
            </CustomButton>
          </div>
        ) : chunkData ? (
          <>
            {/* Chunk list - scrollable */}
            <div className="flex-1 overflow-y-auto space-y-4 pr-2">
              {chunkData.chunks.map((chunk) => (
                <div
                  key={chunk.id}
                  className="border border-gray-200 rounded-lg p-4 bg-gray-50"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-black text-sm font-medium">
                      Chunk {chunk.metadata.chunkIndex + 1} of{" "}
                      {chunkData.chunkCount}
                    </span>
                    <span className="text-gray-500 text-xs">
                      {new Date(chunk.embeddedAt).toLocaleDateString()}
                    </span>
                  </div>
                  {chunk.metadata.section && (
                    <div className="text-gray-700 text-sm font-medium mb-2">
                      {chunk.metadata.section}
                    </div>
                  )}
                  <div className="text-gray-600 text-sm leading-relaxed whitespace-pre-wrap">
                    {chunk.content}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-200 flex-shrink-0">
              <div className="text-gray-600 text-sm">
                Page {chunkData.pagination.page} of{" "}
                {chunkData.pagination.totalPages}
              </div>
              <div className="flex gap-2">
                <CustomButton
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                >
                  Previous
                </CustomButton>
                <CustomButton
                  onClick={handleNextPage}
                  disabled={currentPage >= chunkData.pagination.totalPages}
                >
                  Next
                </CustomButton>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </PopupModal>
  );
}
